import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { cityBySlug, cities } from "@/lib/data/cities";
import { services } from "@/lib/data/services";
import { sectors as allSectors } from "@/lib/data/sectors";
import { neighborhoodsByCity } from "@/lib/data/neighborhoods";
import { extendedCitySections } from "@/lib/data/extended-content";
import { withCanadaKeywords } from "@/lib/data/canada-keywords";
import useSEO from "@/hooks/useSEO";
import RevealText from "@/components/RevealText";
import { ArrowUpRight } from "lucide-react";

const CITY_IMAGE =
  "https://images.unsplash.com/photo-1579454577332-6c8a8f99a23c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w7NDQ2NDJ8MHwxfHNlYXJjaHw0fHx3aW5uaXBlZyUyMHNreWxpbmUlMjB3aW50ZXJ8ZW58MHx8fHwxNzg1MTA1NzIwfDA&ixlib=rb-4.1.0&q=85";

export default function CityDetail() {
  const { slug } = useParams();
  const city = cityBySlug(slug);

  useSEO({
    title: city
      ? `${city.name} Security Guard Services — Manitoba Security Services`
      : "City — Manitoba Security Services",
    description: city ? city.intro : undefined,
    keywords: city ? withCanadaKeywords(city.keywords) : undefined,
    canonical:
      typeof window !== "undefined"
        ? window.location.origin + `/cities/${slug}`
        : `/cities/${slug}`,
    jsonLd: city
      ? {
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          "@id": `https://manitobaguards.ca/cities/${city.slug}`,
          name: `Manitoba Security Services — ${city.name}`,
          description: city.intro,
          areaServed: {
            "@type": "City",
            name: city.name,
            containedInPlace: {
              "@type": "State",
              name: "Manitoba",
              containedInPlace: { "@type": "Country", name: "Canada" },
            },
          },
          geo:
            city.lat && city.lng
              ? {
                  "@type": "GeoCoordinates",
                  latitude: city.lat,
                  longitude: city.lng,
                }
              : undefined,
        }
      : undefined,
  });

  if (!city) return <Navigate to="/cities" replace />;

  const relatedSectors = allSectors.filter((sc) => sc.citySlug === city.slug);
  const cityNeighborhoods = neighborhoodsByCity(city.slug);
  const extended = extendedCitySections(city);
  const combinedSections = [...city.sections, ...extended];

  // Other cities in same region; fall back to primary cities when region-alone.
  let neighbours = cities
    .filter((c) => c.region === city.region && c.slug !== city.slug)
    .slice(0, 6);
  if (neighbours.length === 0) {
    neighbours = cities.filter((c) => c.slug !== city.slug).slice(0, 6);
  }

  return (
    <div className="bg-[#050505] pt-24 md:pt-28">
      {/* Hero */}
      <section className="relative min-h-[75svh] w-full overflow-hidden border-b border-[#1e1e1e]">
        <img
          src={CITY_IMAGE}
          alt={`${city.name}, Manitoba — coverage area`}
          className="absolute inset-0 h-full w-full object-cover grayscale contrast-125 opacity-40"
          loading="eager"
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(ellipse 70% 50% at 30% 50%, transparent 0%, rgba(5,5,5,0.85) 100%)",
          }}
        />

        <div className="relative z-10 mx-auto max-w-[1600px] px-6 md:px-10 pt-16 md:pt-24 pb-16 grid grid-cols-12 gap-6">
          <div className="col-span-12 md:col-span-3">
            <p className="font-editorial italic text-[#FF3B30] text-sm">
              Region
            </p>
            <p className="font-display uppercase tracking-[0.18em] text-white text-sm mt-1">
              {city.region}
            </p>
            {city.lat && (
              <div className="mt-8">
                <p className="font-editorial italic text-[#a0a0a0] text-sm">
                  Coordinates
                </p>
                <p className="font-display text-white text-base">
                  {city.lat.toFixed(3)}° N
                </p>
                <p className="font-display text-white text-base">
                  {Math.abs(city.lng).toFixed(3)}° W
                </p>
              </div>
            )}
          </div>
          <div className="col-span-12 md:col-span-9">
            <p className="font-body uppercase tracking-[0.18em] text-[#a0a0a0] text-xs mb-4">
              Coverage — Manitoba
            </p>
            <RevealText
              lines={[city.name.toUpperCase()]}
              className="font-display font-black uppercase text-white tracking-tightest leading-[0.85] text-[clamp(3rem,14vw,14rem)]"
              stagger={0.1}
              delay={0.1}
            />
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7, duration: 0.9 }}
              className="mt-8 font-body text-lg md:text-xl text-white/90 max-w-3xl leading-relaxed"
            >
              {city.intro}
            </motion.p>
          </div>
        </div>
      </section>

      {/* Sections */}
      <section className="mx-auto max-w-[1600px] px-6 md:px-10 py-20 md:py-32 space-y-20 md:space-y-28">
        {combinedSections.map((sec, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="grid grid-cols-12 gap-6 md:gap-10"
          >
            <div className="col-span-12 md:col-span-4">
              <p className="font-editorial italic text-[#FF3B30] text-sm">
                Chapter {String(i + 1).padStart(2, "0")}
              </p>
              <h3 className="mt-4 font-display font-black uppercase tracking-tight text-white text-2xl md:text-3xl leading-tight">
                {sec.h}
              </h3>
            </div>
            <div className="col-span-12 md:col-span-7 md:col-start-6">
              <p className="font-body text-base md:text-lg text-[#a0a0a0] leading-[1.75] max-w-2xl">
                {sec.p}
              </p>
            </div>
          </motion.div>
        ))}
      </section>

      {/* Services in this city — internal linking */}
      <section className="border-y border-[#1e1e1e] bg-[#0b0b0b] py-20 md:py-28">
        <div className="mx-auto max-w-[1600px] px-6 md:px-10">
          <div className="grid grid-cols-12 gap-6 mb-12">
            <div className="col-span-12 md:col-span-4">
              <p className="font-editorial italic text-[#FF3B30] text-sm">
                Available in {city.name}
              </p>
            </div>
            <div className="col-span-12 md:col-span-8">
              <h3 className="font-display font-black uppercase tracking-tight text-white text-3xl md:text-5xl leading-tight">
                All fifteen{" "}
                <span className="font-editorial italic font-normal text-[#a0a0a0]">
                  disciplines
                </span>{" "}
                in {city.name}
              </h3>
            </div>
          </div>
          <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-3">
            {services.map((s) => (
              <li key={s.slug}>
                <Link
                  to={`/services/${s.slug}/${city.slug}`}
                  data-testid={`city-service-link-${s.slug}`}
                  className="group flex items-baseline justify-between border-b border-[#1e1e1e] py-4 hover:border-[#FF3B30] transition-colors"
                >
                  <span className="font-display uppercase tracking-tight text-white text-sm md:text-base group-hover:translate-x-1 transition-transform">
                    <span className="font-editorial italic text-[#FF3B30] not-italic mr-2">
                      {s.number}
                    </span>
                    {s.name} · {city.name}
                  </span>
                  <ArrowUpRight
                    className="text-white/40 group-hover:text-[#FF3B30]"
                    size={16}
                  />
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* SEO tag band */}
      <section className="py-12 border-b border-[#1e1e1e]">
        <div className="mx-auto max-w-[1600px] px-6 md:px-10">
          <p className="font-editorial italic text-[#FF3B30] text-sm mb-4">
            Directory tags
          </p>
          <ul className="flex flex-wrap gap-x-4 gap-y-2">
            {city.keywords.map((k) => (
              <li
                key={k}
                className="font-body text-xs md:text-sm uppercase tracking-[0.14em] text-[#a0a0a0] border border-[#2a2a2a] px-3 py-1"
              >
                {k}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Neighborhoods within this city */}
      {cityNeighborhoods.length > 0 && (
        <section className="border-t border-[#1e1e1e]">
          <div className="mx-auto max-w-[1600px] px-6 md:px-10 py-20 md:py-24">
            <div className="grid grid-cols-12 gap-6 mb-10">
              <div className="col-span-12 md:col-span-4">
                <p className="font-editorial italic text-[#FF3B30] text-sm">
                  Neighborhoods
                </p>
              </div>
              <div className="col-span-12 md:col-span-8">
                <h3 className="font-display font-black uppercase tracking-tight text-white text-3xl md:text-5xl leading-tight">
                  Coverage inside{" "}
                  <span className="font-editorial italic font-normal text-[#a0a0a0]">
                    {city.name}
                  </span>
                </h3>
              </div>
            </div>
            <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-3">
              {cityNeighborhoods.map((n) => (
                <li key={n.slug}>
                  <Link
                    to={`/cities/${city.slug}/${n.slug}`}
                    data-testid={`city-neighborhood-${n.slug}`}
                    className="group flex items-baseline justify-between border-b border-[#1e1e1e] py-4 hover:border-[#FF3B30] transition-colors"
                  >
                    <div className="min-w-0">
                      <p className="font-display uppercase tracking-tight text-white text-sm md:text-base group-hover:translate-x-1 transition-transform">
                        {n.name}
                      </p>
                      <p className="mt-1 font-editorial italic text-[#6a6a6a] text-xs md:text-sm">
                        {n.tag}
                      </p>
                    </div>
                    <ArrowUpRight
                      className="text-white/40 group-hover:text-[#FF3B30] flex-shrink-0"
                      size={16}
                    />
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {/* Sector programs specific to this city */}
      {relatedSectors.length > 0 && (
        <section className="border-t border-[#1e1e1e]">
          <div className="mx-auto max-w-[1600px] px-6 md:px-10 py-20 md:py-24">
            <div className="grid grid-cols-12 gap-6 mb-10">
              <div className="col-span-12 md:col-span-4">
                <p className="font-editorial italic text-[#FF3B30] text-sm">
                  Sector programs — {city.name}
                </p>
              </div>
              <div className="col-span-12 md:col-span-8">
                <h3 className="font-display font-black uppercase tracking-tight text-white text-3xl md:text-5xl leading-tight">
                  Curated programs{" "}
                  <span className="font-editorial italic font-normal text-[#a0a0a0]">
                    for
                  </span>{" "}
                  {city.name}
                </h3>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {relatedSectors.map((sc) => (
                <Link
                  key={sc.slug}
                  to={`/sectors/${sc.slug}`}
                  data-testid={`city-sector-${sc.slug}`}
                  className="group border border-[#1e1e1e] hover:border-[#FF3B30] p-6 md:p-8 transition-colors block"
                >
                  <p className="font-editorial italic text-[#FF3B30] text-sm">
                    {sc.service?.number}
                  </p>
                  <h4 className="mt-2 font-display uppercase tracking-tight text-white text-lg md:text-xl leading-tight group-hover:translate-x-1 transition-transform">
                    {sc.heading}
                  </h4>
                  <p className="mt-3 font-editorial italic text-[#a0a0a0] text-sm">
                    {sc.lede}
                  </p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Neighbouring cities */}
      {neighbours.length > 0 && (
        <section className="mx-auto max-w-[1600px] px-6 md:px-10 py-20">
          <p className="font-editorial italic text-[#FF3B30] text-sm mb-8">
            Nearby coverage — {city.region}
          </p>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {neighbours.map((n) => (
              <Link
                key={n.slug}
                to={`/cities/${n.slug}`}
                data-testid={`neighbour-${n.slug}`}
                className="group border border-[#1e1e1e] hover:border-[#FF3B30] p-5 transition-colors"
              >
                <p className="font-display uppercase tracking-tight text-white text-base group-hover:translate-x-1 transition-transform">
                  {n.name}
                </p>
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
